import styled from "@emotion/styled"
import { get } from "lodash"
import PropTypes from "prop-types"
import React, { useRef, useState } from "react"
import tw from "twin.macro"
import logoContrast from "../images/laitram-logo-white.svg"
import logo from "../images/laitram-logo.svg"
import { getFirstFocusable, getNextSibling, getPreviousSibling } from "../utils/helpers"
import linkTransformer from "../utils/linkTransformer"
import tailwindConfig from "../utils/tailwindConfig"
import Button from "./Button"
import Link from "./Link"
import MobileMenuButton from "./MobileMenuButton"
import SocialLinks from "./SocialLinks"
import HeaderSearch from "./HeaderSearch"

const HeaderWrapper = tw.div`
  relative z-30
`
const LogoWrapper = styled.div`
  height: 36px;
  width: 126px;
`
const Primary = styled.div`
  ${tw`px-4 lg:px-12 py-4 flex items-center justify-between z-10 relative`}
`

const PrimaryNavWrapper = tw.div`
  hidden lg:block
`

const PrimaryNav = styled.ul`
  ${tw`flex flex-wrap -ml-6 items-center leading-tight`}
`
const StyledPrimaryNavItem = styled.li`
  ${tw`pl-6 uppercase relative`}
`

const NavLink = styled(Link)`
  ${tw`text-blue inline-block tracking-wide transition-all duration-300 ease-in-out hover:text-teal bg-no-repeat py-1`}
  background-image: linear-gradient(to top, currentColor 2px,transparent 2px);
  background-size: 0 100%;
  &:hover,
  &:focus,
  .is-open > &,
  &.active {
    background-size: 100% 100%;
  }
  &.active {
    ${tw`font-bold`}
  }
`

const NavButton = styled(Button)`
  &.active {
    ${tw`bg-black`}
  }
`

const SubNav = styled.ul`
  ${tw`bg-white p-2 absolute shadow-xl border border-gray-light rounded hidden`}
  ${(props) => props.positionX === `right` && tw`right-0`}
  ${(props) => props.positionX === `left` && tw`left-0 ml-6 `}
  top: calc(100% + ${tailwindConfig.theme.spacing["1"]});
  width: 215px;
  ${StyledPrimaryNavItem}.is-open &,
  ${StyledPrimaryNavItem}:active &,
  ${StyledPrimaryNavItem}:focus & {
    ${tw`block`}
  }
  li + li {
    ${tw`pt-1`}
    margin-top: 1px;
  }
`

const SubNavLink = styled(Link)`
  ${tw`text-gray block hover:bg-gray-light transition-colors duration-300 py-1 px-2 text-base`}
  &.active {
    ${tw`bg-blue text-white`}
  }
`

const SubNavButton = styled(SubNavLink)`
  ${tw`border border-blue text-blue rounded hover:bg-blue hover:text-white`}
  &.active {
    ${tw`bg-blue text-white`}
  }
`

const MobileLogo = tw.img`
  w-full max-w-full block lg:hidden
`
const DesktopLogo = tw.img`
  w-full max-w-full hidden lg:block
`

const Secondary = styled.div`
  ${tw`bg-teal py-2 px-4 lg:px-12 justify-end flex items-center`}
  > form {
    ${tw`pr-2 flex-shrink`}
  }
`

const Header = ({ global, append, contrastLogo }) => {
  const primaryNavigationLinks = get(
    global,
    "elements.primary_navigation_links.value"
  )

  return (
    <HeaderWrapper>
      <Primary>
        <LogoWrapper>
          <Link to="/">
            <DesktopLogo
              src={contrastLogo ? logoContrast : logo}
              alt="Laitram"
            />
            <MobileLogo src={logo} alt="Laitram" />
          </Link>
        </LogoWrapper>
        <MobileMenuButton />
        <PrimaryNavWrapper>
          <PrimaryNav>
            {primaryNavigationLinks.map((navItem, index) => {
              const id = get(navItem, "system.id")
              const partiallyActive = index > 0 ? true : false
              return (
                <PrimaryNavItem
                  item={navItem}
                  partiallyActive={partiallyActive}
                  key={id}
                />
              )
            })}
          </PrimaryNav>
        </PrimaryNavWrapper>
      </Primary>
      <Secondary>
        <HeaderSearch />
        <SocialLinks global={global} />
      </Secondary>
      {append}
    </HeaderWrapper>
  )
}

Header.propTypes = {
  global: PropTypes.object,
}

const PrimaryNavItem = ({ item, partiallyActive }) => {
  const transformedLink = linkTransformer(item)
  const style = get(item, "elements.style.value[0].codename") || "link"
  const children = get(item, "elements.children.value") || []
  const hasChildren = children.length ? true : false
  const [isOpen, setIsOpen] = useState(false)
  const [positionX, setPositionX] = useState(null)
  const subNavRef = useRef()

  let timer

  const handleMouseEnter = () => {
    if (!hasChildren) {
      return
    }

    setIsOpen(true)

    if (!positionX) {
      const whenVisible = setTimeout(() => {
        const bounding = subNavRef.current.getBoundingClientRect()
        if (bounding.width > 0) {
          if (
            bounding.right >
            (window.innerWidth || document.documentElement.clientWidth)
          ) {
            setPositionX("right")
          } else {
            setPositionX("left")
          }
          clearTimeout(whenVisible)
        } else {
          whenVisible()
        }
      }, 1)
    }

    clearTimeout(timer)
  }

  const handleMouseLeave = () => {
    if (!hasChildren) {
      return
    }

    timer = setTimeout(() => {
      setIsOpen(false)
      setPositionX(null)
    }, 300)
  }

  let previousActiveElement

  const handleKeyPress = (event, context = "topLevel") => {
    // Remeber the top level item so focus can be restored when the dropdown is closed
    if (context === "topLevel") {
      previousActiveElement = document.activeElement
    }

    if (context === "topLevel" && !hasChildren) {
      return
    }

    switch (event.keyCode) {
      case 9: // tab
        // Pressing tab should select the next top level item and close the dropdown
        setIsOpen(false)
        break
      case 13: // enter
      case 32: // space
        // Pressing enter or space on a top level item should open the dropdown
        if (context === "topLevel") {
          setIsOpen(!isOpen)
          event.preventDefault()
        }
        break
      case 27: // esc
        // Pressing esc should close the dropdown and restore focus to the top level item
        setIsOpen(false)
        previousActiveElement && previousActiveElement.focus()
        break
      case 38: // up
        // Pressing up should select the previous item in the dropdown or the top level item if the first item in the dropdown is selected
        event.preventDefault()
        if (context === "topLevel") {
          return
        } else {
          const listItem = event.currentTarget.closest("li")
          const prevItem = getPreviousSibling(listItem, "li")
          if (prevItem) {
            const firstFocusableElem = getFirstFocusable(prevItem, "li")
            firstFocusableElem && firstFocusableElem.focus()
          } else {
            previousActiveElement && previousActiveElement.focus()
          }
        }
        break
      case 40: // down
        // Pressing down should select the next item in the dropdown
        event.preventDefault()
        if (context === "topLevel") {
          getFirstFocusable(getNextSibling(event.currentTarget), "ul").focus()
        } else {
          const listItem = event.currentTarget.closest("li")
          const nextItem = getNextSibling(listItem, "li")
          if (nextItem) {
            const firstFocusableElem = getFirstFocusable(nextItem, "li")
            firstFocusableElem && firstFocusableElem.focus()
          }
        }
        break
      default:
        return
    }
  }

  const topLevelProps = {
    to: transformedLink.to,
    openInNewWindow: transformedLink.openInNewWindow,
    activeClassName: "active",
    partiallyActive: partiallyActive,
    onKeyDown: (e) => handleKeyPress(e),
    "aria-haspopup": children.length ? `true` : undefined,
    "aria-expanded": children.length ? (isOpen ? `true` : `false`) : undefined,
  }

  return (
    <StyledPrimaryNavItem
      isOpen={isOpen}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={isOpen ? `is-open` : ``}
    >
      {
        {
          link: <NavLink {...topLevelProps}>{transformedLink.label}</NavLink>,
          button: (
            <NavButton {...topLevelProps} as={Link}>
              {transformedLink.label}
            </NavButton>
          ),
        }[style]
      }
      {children.length ? (
        <SubNav ref={subNavRef} positionX={positionX}>
          {children.map((child, index) => {
            const transformedChildLink = linkTransformer(child)
            const childId = get(child, "system.id")
            const childStyle =
              get(child, "elements.style.value[0].codename") || "link"
            const childProps = {
              to: transformedChildLink.to,
              openInNewWindow: transformedChildLink.openInNewWindow,
              activeClassName: "active",
              onKeyDown: (e) => handleKeyPress(e, "child"),
            }
            return (
              <li key={childId}>
                {
                  {
                    link: (
                      <SubNavLink {...childProps}>
                        {transformedChildLink.label}
                      </SubNavLink>
                    ),
                    button: (
                      <SubNavButton {...childProps} as={Link}>
                        {transformedChildLink.label}
                      </SubNavButton>
                    ),
                  }[childStyle]
                }
              </li>
            )
          })}
        </SubNav>
      ) : null}
    </StyledPrimaryNavItem>
  )
}

export default Header
